import React, { useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { v1Get, v1Put, v1Post } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Save, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  TECH_DIFFICULTIES,
  matchesTechQuestionFilters,
  formatApiError,
} from './pipelineUtils';
import {
  TechQuestionMeta,
  PipelineFilterMultiSelect,
  SelectRandomQuestionsDialog,
} from './PipelineBindingShared';
import { Gated } from '@/auth/permissions';

export function MultiPanelBinding({ pipelineId, pipelineLabel, departmentId, panelStages }) {
  const rounds = panelStages || [];
  const withId = rounds.filter((s) => s.id);
  const [activeStageId, setActiveStageId] = useState(withId[0]?.id || null);
  const withIdKey = withId.map((s) => s.id).join('|');

  React.useEffect(() => {
    if (!withId.length) {
      setActiveStageId(null);
      return;
    }
    if (!withId.some((s) => s.id === activeStageId)) {
      setActiveStageId(withId[0].id);
    }
  }, [pipelineId, withIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rounds.length) {
    return (
      <div className="mt-5 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">
        Add a Tech Panel stage on the Stages tab to bind questions.
      </div>
    );
  }

  const pending = rounds.filter((s) => !s.id);
  const active = withId.find((s) => s.id === activeStageId) || withId[0];

  return (
    <div className="space-y-3" data-testid="pipeline-multi-panel-binding">
      {pending.length > 0 && (
        <p className="rounded-lg border border-dashed bg-amber-50/60 p-3 text-xs text-amber-900">
          {pending.length === 1
            ? `"${pending[0].label || 'Panel'}" is new — Save version on the Stages tab before binding questions for that round.`
            : `${pending.length} new panel rounds need Save version before you can bind questions for them.`}
        </p>
      )}
      {withId.length === 0 ? (
        <div className="mt-2 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">
          Save the pipeline version to create stage ids, then bind questions per round.
        </div>
      ) : (
        <>
          {withId.length > 1 && (
            <Tabs value={active?.id} onValueChange={setActiveStageId}>
              <TabsList className="flex h-auto flex-wrap gap-1">
                {withId.map((s, i) => (
                  <TabsTrigger
                    key={s.id}
                    value={s.id}
                    data-testid={`pipeline-panel-round-tab-${i}`}
                  >
                    {s.label || `Panel ${i + 1}`}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
          {active?.id && (
            <PanelBinding
              key={active.id}
              pipelineId={pipelineId}
              pipelineLabel={pipelineLabel}
              departmentId={departmentId}
              stageId={active.id}
              stageLabel={active.label || 'Tech Panel'}
              siblingStageIds={withId.filter((s) => s.id !== active.id).map((s) => s.id)}
            />
          )}
        </>
      )}
    </div>
  );
}

export function PanelBinding({ pipelineId, pipelineLabel, departmentId, stageId, stageLabel, siblingStageIds = [] }) {
  const qc = useQueryClient();
  const { data: binding } = useQuery({
    queryKey: ['pipeline-stage-panel-binding', pipelineId, stageId],
    queryFn: () => v1Get(`/pipelines/${pipelineId}/stages/${stageId}/panel-questions`),
    enabled: !!pipelineId && !!stageId,
  });
  const panelQueryKey = ['v1-panel-qs-pipeline', departmentId];
  const { data: scopedQs } = useQuery({
    queryKey: panelQueryKey,
    queryFn: () => {
      const params = new URLSearchParams({ status: 'active' });
      if (departmentId) params.set('department_id', departmentId);
      return v1Get(`/questions/panel?${params.toString()}`);
    },
    enabled: !!pipelineId,
  });
  const { data: allQs } = useQuery({
    queryKey: ['v1-panel-qs-all'],
    queryFn: () => v1Get('/questions/panel'),
    enabled: !!pipelineId,
  });
  const { data: workCategories = [] } = useQuery({
    queryKey: ['v1-work-categories', departmentId],
    queryFn: () => v1Get(`/questions/work-categories?department_id=${departmentId}`),
    enabled: !!pipelineId && !!departmentId,
  });
  const siblingBindings = useQueries({
    queries: siblingStageIds.map((sid) => ({
      queryKey: ['pipeline-stage-panel-binding', pipelineId, sid],
      queryFn: () => v1Get(`/pipelines/${pipelineId}/stages/${sid}/panel-questions`),
      enabled: !!pipelineId && !!sid,
    })),
  });
  const siblingBoundIds = React.useMemo(() => {
    const ids = new Set();
    for (const q of siblingBindings) {
      for (const qid of q.data?.question_ids || []) ids.add(qid);
    }
    return ids;
  }, [siblingBindings]);
  const [draft, setDraft] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [difficultyFilter, setDifficultyFilter] = useState([]);
  const [randomOpen, setRandomOpen] = useState(false);
  const [randomCount, setRandomCount] = useState('1');
  const [randomLoading, setRandomLoading] = useState(false);
  React.useEffect(() => {
    if (binding) setDraft(binding.question_ids);
  }, [binding, pipelineId, stageId]);
  React.useEffect(() => {
    setCategoryFilter([]);
    setDifficultyFilter([]);
  }, [pipelineId, departmentId, stageId]);

  const byId = React.useMemo(() => {
    const map = new Map();
    for (const q of allQs || []) map.set(q.id, q);
    for (const q of scopedQs || []) map.set(q.id, q);
    return map;
  }, [allQs, scopedQs]);

  const activeScoped = (scopedQs || []).filter((q) => q.status === 'active');
  const siblingsPending = siblingBindings.some((q) => q.isPending);
  if (!binding || !scopedQs || !allQs || draft === null || siblingsPending) {
    return (
      <div className="mt-5 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">
        Loading Tech Panel question bindings…
      </div>
    );
  }

  const inSet = new Set(draft);
  const ordered = draft.map((qid) => byId.get(qid)).filter(Boolean);
  const eligibleForRandom = activeScoped.filter((q) => !siblingBoundIds.has(q.id));
  const available = activeScoped
    .filter((q) => !inSet.has(q.id))
    .filter((q) => !siblingBoundIds.has(q.id))
    .filter((q) => matchesTechQuestionFilters(q, categoryFilter, difficultyFilter));
  const randomMax = Math.min(50, eligibleForRandom.length);
  const hiddenBySiblings = activeScoped.some((q) => !inSet.has(q.id) && siblingBoundIds.has(q.id));
  const categoryOptions = workCategories.map((c) => ({ value: c.label, label: c.label }));
  const difficultyOptions = TECH_DIFFICULTIES.map((d) => ({
    value: d,
    label: d.charAt(0).toUpperCase() + d.slice(1),
  }));
  const filtersActive = categoryFilter.length > 0 || difficultyFilter.length > 0;
  const selectRandom = async () => {
    if (draft.length && !window.confirm('Replace the current draft with a new random selection?')) return;
    setRandomLoading(true);
    try {
      const out = await v1Post(`/pipelines/${pipelineId}/stages/${stageId}/panel-questions/sample`, {
        count: Number(randomCount),
      });
      setDraft(out.question_ids || []);
      setRandomOpen(false);
      toast.success(`Selected ${out.count || 0} Tech Panel questions`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, 'Random selection failed'));
    } finally {
      setRandomLoading(false);
    }
  };
  const move = (from, to) => {
    if (to < 0 || to >= ordered.length) return;
    const next = [...ordered];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setDraft(next.map((q) => q.id));
  };
  const save = async () => {
    try {
      await v1Put(`/pipelines/${pipelineId}/stages/${stageId}/panel-questions`, { question_ids: draft });
      qc.invalidateQueries({ queryKey: ['pipeline-stage-panel-binding', pipelineId] });
      toast.success(`Tech Panel questions saved for ${stageLabel}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    }
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(binding.question_ids);

  return (
    <div className="mt-5 rounded-xl border bg-secondary/40 p-4" data-testid="pipeline-panel-binding">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {stageLabel} questions · {pipelineLabel}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Interviewers will use these checklist prompts for this panel round, in this order.
            {departmentId ? ' Showing active questions for this pipeline department.' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Gated need="pipeline.edit">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setRandomOpen(true)}
              disabled={randomMax < 1}
              data-testid="pipeline-panel-random-open"
            >
              Select random questions
            </Button>
            <Gated need="bank.panel.view">
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to="/portal/settings/questions/panel"><FileText className="h-3.5 w-3.5" /> Manage bank</Link>
              </Button>
            </Gated>
            <Button size="sm" onClick={save} disabled={!dirty} className="gap-1.5" data-testid="pipeline-panel-binding-save">
              <Save className="h-3.5 w-3.5" /> Save binding
            </Button>
          </Gated>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="flex max-h-[min(50vh,420px)] flex-col overflow-hidden rounded-lg border bg-card p-3">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">In this round ({ordered.length})</p>
          {ordered.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">No questions yet. Add some from the right panel.</p>
          ) : (
            <ol className="mt-2 min-h-0 flex-1 space-y-1.5 overflow-y-auto">
              {ordered.map((q, i) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2" data-testid={`pipeline-panel-bound-${i}`}>
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs leading-snug">{q.text}</span>
                    <TechQuestionMeta topic={q.topic} difficulty={q.difficulty} />
                  </div>
                  <div className="flex shrink-0 flex-col gap-1 text-muted-foreground">
                    <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="text-[10px] disabled:opacity-30">▲</button>
                    <button type="button" onClick={() => move(i, i + 1)} disabled={i === ordered.length - 1} className="text-[10px] disabled:opacity-30">▼</button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft(draft.filter((id) => id !== q.id))} className="-mr-1 -mt-1 h-6 w-6 p-0"><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="flex max-h-[min(50vh,420px)] flex-col overflow-hidden rounded-lg border bg-card p-3">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Available active questions ({available.length})</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <PipelineFilterMultiSelect
                options={categoryOptions}
                value={categoryFilter}
                onChange={setCategoryFilter}
                placeholder="All categories"
                disabled={!departmentId}
                testId="pipeline-panel-category-filter"
                compact
              />
              <PipelineFilterMultiSelect
                options={difficultyOptions}
                value={difficultyFilter}
                onChange={setDifficultyFilter}
                placeholder="All difficulties"
                testId="pipeline-panel-difficulty-filter"
                compact
              />
              {filtersActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => { setCategoryFilter([]); setDifficultyFilter([]); }}
                  data-testid="pipeline-panel-filter-reset"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
          {available.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {filtersActive
                ? 'No active questions match the selected category and difficulty filters.'
                : hiddenBySiblings
                  ? 'All remaining questions are already assigned to other Technical Panel rounds in this pipeline.'
                  : (
                  <>
                    No matching active questions.
                    <Gated need="bank.panel.view">
                      {' '}
                      <Link to="/portal/settings/questions/panel" className="font-semibold text-primary underline">Add some in the Tech Panel bank</Link>
                      .
                    </Gated>                  </>
                )}
            </p>
          ) : (
            <ul className="mt-2 min-h-0 flex-1 space-y-1.5 overflow-y-auto">
              {available.map((q) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2">
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs leading-snug">{q.text}</span>
                    <TechQuestionMeta topic={q.topic} difficulty={q.difficulty} />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft([...draft, q.id])} className="gap-1 text-[11px]" data-testid={`pipeline-panel-add-${q.id}`}><Plus className="h-3 w-3" /> Add</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <SelectRandomQuestionsDialog
        open={randomOpen}
        onOpenChange={setRandomOpen}
        title={`Select random Tech Panel questions for ${stageLabel}`}
        description="A balanced sample is drawn from active questions in this pipeline’s department that are not already assigned to another Technical Panel round."
        count={randomCount}
        setCount={setRandomCount}
        maxCount={randomMax}
        onConfirm={selectRandom}
        loading={randomLoading}
        testIdPrefix="pipeline-panel-random"
      />
    </div>
  );
}

export function OfferBinding({ pipelineId, pipelineLabel }) {
  const qc = useQueryClient();
  const { data: binding } = useQuery({
    queryKey: ['pipeline-offer-binding', pipelineId],
    queryFn: () => v1Get(`/pipelines/${pipelineId}/offer-questions`),
    enabled: !!pipelineId,
  });
  const { data: allQs } = useQuery({
    queryKey: ['v1-offer-qs-all'],
    queryFn: () => v1Get('/questions/offer'),
  });
  const [draft, setDraft] = useState(null);
  const [topicFilter, setTopicFilter] = useState([]);
  const [randomOpen, setRandomOpen] = useState(false);
  const [randomCount, setRandomCount] = useState('1');
  const [randomLoading, setRandomLoading] = useState(false);
  React.useEffect(() => {
    if (binding) {
      setDraft(binding.question_ids);
      setTopicFilter(binding.topic_filters || []);
    }
  }, [binding, pipelineId]);

  const activeQs = (allQs || []).filter((q) => q.status === 'active');
  const topicOptions = React.useMemo(() => {
    const topics = new Set();
    for (const q of activeQs) {
      if (q.topic) topics.add(q.topic);
    }
    return [...topics].sort().map((t) => ({ value: t, label: t }));
  }, [activeQs]);
  if (!binding || !allQs || draft === null) {
    return (
      <div className="mt-5 rounded-xl border bg-secondary/40 p-4 text-xs text-muted-foreground">
        Loading Offer question bindings…
      </div>
    );
  }

  const inSet = new Set(draft);
  const ordered = draft.map((qid) => activeQs.find((q) => q.id === qid)).filter(Boolean);
  const available = activeQs
    .filter((q) => !inSet.has(q.id))
    .filter((q) => matchesTechQuestionFilters(q, topicFilter, []));
  const randomMax = Math.min(50, activeQs.length);
  const filtersActive = topicFilter.length > 0;
  const selectRandom = async () => {
    if (draft.length && !window.confirm('Replace the current draft with a new random selection?')) return;
    setRandomLoading(true);
    try {
      const out = await v1Post(`/pipelines/${pipelineId}/offer-questions/sample`, {
        count: Number(randomCount),
      });
      setDraft(out.question_ids || []);
      setRandomOpen(false);
      toast.success(`Selected ${out.count || 0} Offer questions`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, 'Random selection failed'));
    } finally {
      setRandomLoading(false);
    }
  };
  const move = (from, to) => {
    if (to < 0 || to >= ordered.length) return;
    const next = [...ordered];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setDraft(next.map((q) => q.id));
  };
  const save = async () => {
    try {
      await v1Put(`/pipelines/${pipelineId}/offer-questions`, {
        question_ids: draft,
        topic_filters: topicFilter,
      });
      qc.invalidateQueries({ queryKey: ['pipeline-offer-binding', pipelineId] });
      toast.success('Offer question binding saved');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    }
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(binding.question_ids)
    || JSON.stringify(topicFilter) !== JSON.stringify(binding.topic_filters || []);

  return (
    <div className="mt-5 rounded-xl border bg-secondary/40 p-4" data-testid="pipeline-offer-binding">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Offer questions for {pipelineLabel}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            HR will use these checklist prompts during the offer conversation, in this order.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Gated need="pipeline.edit">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setRandomOpen(true)}
              disabled={randomMax < 1}
              data-testid="pipeline-offer-random-open"
            >
              Select random questions
            </Button>
            <Gated need="bank.offer.view">
              <Button asChild variant="outline" size="sm" className="gap-1.5">
                <Link to="/portal/settings/questions/offer"><FileText className="h-3.5 w-3.5" /> Manage bank</Link>
              </Button>
            </Gated>
            <Button size="sm" onClick={save} disabled={!dirty} className="gap-1.5" data-testid="pipeline-offer-binding-save">
              <Save className="h-3.5 w-3.5" /> Save binding
            </Button>
          </Gated>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="flex max-h-[min(50vh,420px)] flex-col overflow-hidden rounded-lg border bg-card p-3">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">In this pipeline ({ordered.length})</p>
          {ordered.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">No questions yet. Add some from the right panel.</p>
          ) : (
            <ol className="mt-2 min-h-0 flex-1 space-y-1.5 overflow-y-auto">
              {ordered.map((q, i) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2" data-testid={`pipeline-offer-bound-${i}`}>
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs leading-snug">{q.text}</span>
                    <TechQuestionMeta topic={q.topic} />
                  </div>
                  <div className="flex shrink-0 flex-col gap-1 text-muted-foreground">
                    <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="text-[10px] disabled:opacity-30">▲</button>
                    <button type="button" onClick={() => move(i, i + 1)} disabled={i === ordered.length - 1} className="text-[10px] disabled:opacity-30">▼</button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft(draft.filter((id) => id !== q.id))} className="-mr-1 -mt-1 h-6 w-6 p-0"><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="flex max-h-[min(50vh,420px)] flex-col overflow-hidden rounded-lg border bg-card p-3">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Available active questions ({available.length})</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <PipelineFilterMultiSelect
                options={topicOptions}
                value={topicFilter}
                onChange={setTopicFilter}
                placeholder="All topics"
                testId="pipeline-offer-topic-filter"
                compact
              />
              {filtersActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => setTopicFilter([])}
                  data-testid="pipeline-offer-filter-reset"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
          {available.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {filtersActive
                ? 'No active questions match the selected topic filters.'
                : (
                  <>
                    Every active question is already bound.
                    <Gated need="bank.offer.view">
                      {' '}
                      <Link to="/portal/settings/questions/offer" className="font-semibold text-primary underline">Add more in the bank</Link>
                      .
                    </Gated>                  </>
                )}
            </p>
          ) : (
            <ul className="mt-2 min-h-0 flex-1 space-y-1.5 overflow-y-auto">
              {available.map((q) => (
                <li key={q.id} className="flex items-start gap-2 rounded-md border bg-secondary/30 p-2">
                  <div className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs leading-snug">{q.text}</span>
                    <TechQuestionMeta topic={q.topic} />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDraft([...draft, q.id])} className="gap-1 text-[11px]" data-testid={`pipeline-offer-add-${q.id}`}><Plus className="h-3 w-3" /> Add</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <SelectRandomQuestionsDialog
        open={randomOpen}
        onOpenChange={setRandomOpen}
        title={`Select random Offer questions for ${pipelineLabel}`}
        description="A balanced sample is drawn from all active offer questions, independent of the topic filter on the right."
        count={randomCount}
        setCount={setRandomCount}
        maxCount={randomMax}
        onConfirm={selectRandom}
        loading={randomLoading}
        testIdPrefix="pipeline-offer-random"
      />
    </div>
  );
}
